import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { fetchMaker, fetchProducts } from "@/lib/api";
import { resolveImageUrl, whatsappLink } from "@/lib/whatsapp";
import { BRAND } from "@/lib/constants";

export default function MakerProfile() {
  const { slug } = useParams();
  const [maker, setMaker] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const m = await fetchMaker(slug);
        if (!mounted) return;
        setMaker(m);
        document.title = `${m.full_name} — OVI`;
        try {
          const ps = await fetchProducts({ maker: m.first_name });
          if (mounted) setProducts(ps);
        } catch (e) {
          console.error(e);
        }
      } catch (e) {
        if (mounted) setError("Maker not found.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[var(--ovi-muted-ink)]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !maker) {
    return (
      <div className="min-h-[60vh] mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20 text-center" data-testid="maker-not-found">
        <h1 className="font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)]">Maker not found.</h1>
        <p className="mt-3 text-[var(--ovi-muted-ink)]">The maker profile you're looking for doesn't exist or has moved.</p>
        <Button asChild className="mt-6 bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] rounded-full h-11 px-6">
          <Link to="/makers">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to all makers
          </Link>
        </Button>
      </div>
    );
  }

  const whatsappMessage = `Hi OVI — I'd like to talk directly to ${maker.full_name} (${maker.specialty}) from ${maker.city}.`;
  const waHref = whatsappLink(whatsappMessage);

  return (
    <div className="bg-[var(--ovi-cream)]">
      {/* Profile header */}
      <section className="border-b border-[var(--ovi-border)] ovi-texture">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <Link
            to="/makers"
            className="inline-flex items-center gap-2 text-sm text-[var(--ovi-indigo)] hover:text-[var(--ovi-terracotta)] transition-colors"
            data-testid="maker-profile-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            All makers
          </Link>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-start">
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
                className="relative rounded-2xl overflow-hidden border border-[var(--ovi-border)] aspect-[4/5]"
              >
                <img
                  src={resolveImageUrl(maker.photo_url)}
                  alt={`${maker.full_name}, ${maker.specialty} — ${maker.city}`}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="eager"
                  data-testid="maker-profile-photo"
                />
              </motion.div>
            </div>
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--ovi-terracotta)] font-semibold">
                {maker.specialty} · {maker.city}
              </p>
              <h1
                className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[var(--ovi-indigo)] tracking-tight leading-[1.05]"
                data-testid="maker-profile-name"
              >
                {maker.full_name}
              </h1>
              {maker.years_of_craft > 0 ? (
                <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.18em] text-[var(--ovi-muted-ink)] font-medium">
                  {maker.years_of_craft}+ years at the loom
                </p>
              ) : null}
              <blockquote
                className="mt-6 font-serif-display italic text-xl sm:text-2xl text-[var(--ovi-ink)] leading-snug border-l-2 border-[var(--ovi-terracotta)] pl-5"
                data-testid="maker-profile-quote"
              >
                “{maker.quote}”
              </blockquote>
              <div className="mt-7 space-y-4 text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed">
                {(maker.story_paragraphs || []).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <div className="mt-8">
                <Button
                  asChild
                  className="bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] rounded-full h-12 px-7 active:scale-[0.98] transition-[background-color,transform] duration-200"
                >
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="maker-profile-whatsapp-button"
                  >
                    <WhatsAppIcon className="h-4 w-4 mr-2" />
                    Message {maker.first_name} on WhatsApp
                  </a>
                </Button>
                <p
                  className="mt-2 text-xs italic text-[var(--ovi-muted-ink)]"
                  data-testid="maker-profile-whatsapp-launch-note"
                >
                  {BRAND.whatsappLaunchNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maker's products */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="mb-8 sm:mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--ovi-terracotta)] font-semibold mb-3">
            {maker.first_name}'s work
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)]">
            From {maker.first_name}'s loom.
          </h2>
        </div>
        {products.length === 0 ? (
          <p className="text-[var(--ovi-muted-ink)]" data-testid="maker-profile-no-products">
            New stock from {maker.first_name} arriving soon.
          </p>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6"
            data-testid="maker-profile-products-grid"
          >
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
        <div className="mt-10">
          <Button
            asChild
            variant="outline"
            className="border-[var(--ovi-indigo)] text-[var(--ovi-indigo)] rounded-full h-11 px-6"
          >
            <Link to="/makers" data-testid="maker-profile-all-makers-link">
              <ArrowLeft className="mr-2 h-4 w-4" />
              See all OVI makers
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
